/**
 * Main widget container
 * Formats the data and render graph
 */
import React from 'react';
import styled, { useTheme } from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { get, isEmpty } from 'lodash';
import getSymbolFromCurrency from 'currency-symbol-map';

import { lighten } from 'utils/colours';
import { isDatasetEmpty } from './utils';
import messages, { graphLabels } from './messages';

import Graph from './components/Graph';
import Footer from './components/Footer';

const Container = styled.div`
    padding: 15px;
    width: 100%;
    text-align: center;
`;
const GraphContainer = styled.div`
    display: flex;
    justify-content: space-between;

    > div {
        width: 100%;
    }
`;

const GraphTitle = styled.div`
    text-align: center;
    font-family: ${({ theme }) => theme.font.regular};
    text-transform: uppercase;
`;

export const Widget = ({ data, currency }) => {
    const { formatNumber, formatMessage } = useIntl();
    const theme = useTheme();

    if (isEmpty(data) || isDatasetEmpty(data)) {
        return (
            <Container>
                <FormattedMessage {...messages.noData} />
            </Container>
        );
    }

    const formatValue = (type, value) => {
        if (type === 'rooms') return formatNumber(value);
        const currencySymbol = getSymbolFromCurrency(currency);
        const currencyValue = formatNumber(value / 100, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });

        return `${currencySymbol}${currencyValue}`;
    };

    const makeBarsDefinition = (type) => {
        const key = type === 'rooms' ? 'rooms' : 'revenue.accommodation';
        return [
            {
                title: formatMessage(graphLabels.currentTitle),
                guaranteedBar: {
                    colour: theme.chartsPalette.cyan,
                    value: get(data, `current.${key}.guaranteed`),
                },
                nonGuaranteedBar: {
                    colour: theme.chartsPalette.yellow,
                    value: get(data, `current.${key}.nonGuaranteed`),
                },
            },
            {
                title: formatMessage(graphLabels.lySpitTitle),
                guaranteedBar: {
                    colour: theme.chartsPalette.blue,
                    value: get(data, `lySpit.${key}.guaranteed`),
                },
                nonGuaranteedBar: {
                    colour: theme.chartsPalette.yellow,
                    value: get(data, `lySpit.${key}.nonGuaranteed`),
                },
            },
            {
                title: formatMessage(graphLabels.lyFinalTitle),
                guaranteedBar: {
                    colour: theme.chartsPalette.purple,
                    value: get(data, `lyFinal.${key}.guaranteed`),
                },
            },
            {
                title: formatMessage(graphLabels.y2SpitTitle),
                guaranteedBar: {
                    colour: lighten(theme.chartsPalette.blue, 20),
                    value: get(data, `y2Spit.${key}.guaranteed`),
                },
                nonGuaranteedBar: {
                    colour: lighten(theme.chartsPalette.yellow, 20),
                    value: get(data, `y2Spit.${key}.nonGuaranteed`),
                },
            },
            {
                title: formatMessage(graphLabels.y2FinalTitle),
                guaranteedBar: {
                    colour: lighten(theme.chartsPalette.purple, 20),
                    value: get(data, `y2Final.${key}.guaranteed`),
                },
            },
        ];
    };

    const makeLabelFormatter = (type) => (value) => (value === null ? null : formatValue(type, value));
    const makeTooltipFormatter = (type) => (value, label) =>
        formatMessage(graphLabels[label], {
            value: formatValue(type, value),
        });

    return (
        <Container>
            <GraphContainer>
                <div>
                    <GraphTitle>
                        <FormattedMessage {...messages.roomGraphTitle} />
                    </GraphTitle>
                    <Graph
                        syncId="RoomsRevenueBusinessStatusSyncId"
                        data={makeBarsDefinition('rooms')}
                        labelFormatter={makeLabelFormatter('rooms')}
                        tooltipFormatter={makeTooltipFormatter('rooms')}
                    />
                </div>
                <div>
                    <GraphTitle>
                        <FormattedMessage {...messages.revenueGraphTitle} />
                    </GraphTitle>
                    <Graph
                        syncId="RoomsRevenueBusinessStatusSyncId"
                        data={makeBarsDefinition('revenue')}
                        labelFormatter={makeLabelFormatter('revenue')}
                        tooltipFormatter={makeTooltipFormatter('revenue')}
                    />
                </div>
            </GraphContainer>
            <Footer data={data} currency={currency} />
        </Container>
    );
};

export default Widget;
